import React, {useState, useEffect, useCallback} from 'react';
import {Dropdown, DropdownOptionProps, TPAComponentsProvider} from 'wix-ui-tpa';
import {CurrencySymbols, GetCurrencyDisplayedValue} from '../../../settings/services/currency.utility';
import {classes as st} from './CurrenciesDropdown.st.css';
import {BiParams, TranslationMap} from '../../../types/common';
import {useCountryInfoHook} from '../../hooks/useCountryInfoHook';
import {PopoverWrapper} from '../PopoverWrapper/PopoverWrapper';
import {useBIEvents} from './useBIEvents';
import {LogObject} from '@wix/bi-logger-payments-data/v2/types';

const processTranslation = (translation: string, params?: Record<string, any>): string => {
  if (!params || !translation) {
    return translation;
  }

  return Object.keys(params).reduce((result, paramKey) => {
    const placeholder = `{${paramKey}}`;
    return result.replace(new RegExp(placeholder, 'g'), params[paramKey]);
  }, translation);
};

const createCurrencyOptions = (
  currencies: string[],
  currencySymbols: CurrencySymbols,
  defaultCurrencyText: string
): DropdownOptionProps[] => {
  return currencies.map((currency) => ({
    id: currency,
    value: GetCurrencyDisplayedValue(currency, currencySymbols, defaultCurrencyText),
    isSelectable: true,
  }));
};

const shouldDisplayPopover = (
  shouldShowSuggestCurrency: boolean,
  isOpen: boolean,
  suggestedCurrencyCode: string,
  selectedCurrency: string,
  currencies: string[]
): boolean => {
  return (
    shouldShowSuggestCurrency &&
    isOpen &&
    suggestedCurrencyCode &&
    suggestedCurrencyCode !== selectedCurrency &&
    currencies?.includes(suggestedCurrencyCode)
  );
};

const useCurrencyDropdownState = (
  currencies: string[],
  currencySymbols: CurrencySymbols,
  defaultCurrencyText: string,
  onChange: (code: string) => void
) => {
  const [currencyOptions, setCurrencyOptions] = useState<DropdownOptionProps[]>(() =>
    createCurrencyOptions(currencies || [], currencySymbols || {}, defaultCurrencyText)
  );

  useEffect(() => {
    setCurrencyOptions(createCurrencyOptions(currencies || [], currencySymbols || {}, defaultCurrencyText));
  }, [currencies, currencySymbols, defaultCurrencyText]);

  useEffect(() => {
    const storedSelectedCurrency = localStorage.getItem('selectedCurrency');
    if (storedSelectedCurrency && currencies?.includes(storedSelectedCurrency)) {
      onChange(storedSelectedCurrency);
    }
  }, [onChange, currencies]);

  return {currencyOptions};
};

export interface CurrenciesDropdownProps {
  currencies: string[];
  selectedCurrency: string;

  onChange: (code: string) => void;

  currencySymbols: CurrencySymbols;
  isMobile: boolean;
  isRtl: boolean;
  translations: TranslationMap;
  style?: {height?: number; width?: number};
  shouldShowSuggestCurrency: boolean;
  suggestedCurrencyCode: string;
  biParams: BiParams;
  onBIEvent: (event: LogObject<unknown>) => void;
}

export const CurrenciesDropdown = (props: CurrenciesDropdownProps) => {
  const {
    currencies,
    selectedCurrency,
    onChange,
    currencySymbols,
    isMobile,
    isRtl,
    translations,
    shouldShowSuggestCurrency,
    suggestedCurrencyCode,
    biParams,
    onBIEvent,
  } = props;

  const [isOpen, setIsOpen] = useState(true);
  const [isBIEventSentOnLoad, setIsBIEventSentOnLoad] = useState(false);
  const {userCountryInfo} = useCountryInfoHook(suggestedCurrencyCode);

  const {sendBIEventOnPopoverShow, sendBIEventOnPopoverClick, sendBIEventWidgetLoaded} = useBIEvents();

  useEffect(() => {
    if (selectedCurrency && !isBIEventSentOnLoad) {
      onBIEvent(sendBIEventWidgetLoaded({biParams, selectedCurrency}));
      setIsBIEventSentOnLoad(true);
    }
  }, [biParams, selectedCurrency, onBIEvent, sendBIEventWidgetLoaded, isBIEventSentOnLoad]);

  const getTranslationByKey = useCallback(
    (key: string, params?: Record<string, any>) => {
      const translation = translations?.[key] || '';
      return processTranslation(translation, params);
    },
    [translations]
  );

  const sendBIEventOnButtonClick = useCallback(
    (isSwitchingToNewCurrency: boolean) => {
      onBIEvent(
        sendBIEventOnPopoverClick({
          biParams,
          selectedCurrency,
          suggestedCurrencyCode,
          cta: isSwitchingToNewCurrency ? 'switch_to' : 'keep',
        })
      );
    },
    [biParams, selectedCurrency, suggestedCurrencyCode, sendBIEventOnPopoverClick, onBIEvent]
  );

  const defaultCurrencyText = getTranslationByKey('currencyConverter.dropdown.defaultCurrency.text');
  const {currencyOptions} = useCurrencyDropdownState(currencies, currencySymbols, defaultCurrencyText, onChange);

  const dropdownKey = GetCurrencyDisplayedValue(selectedCurrency, currencySymbols, defaultCurrencyText);

  const handleContainerClick = () => setIsOpen(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setIsOpen(false);
    }
  };

  const handleDropdownChange = (option: DropdownOptionProps) => {
    onChange(option.id);
    localStorage.setItem('selectedCurrency', option.id);
  };

  const widgetAriaLabel = getTranslationByKey('sr.currencyConverter.widget');

  const getDropdown = () => (
    <div
      data-hook="currency-converter-container"
      className={st.element}
      role="button"
      tabIndex={0}
      onClick={handleContainerClick}
      onKeyDown={handleKeyDown}
      aria-label={widgetAriaLabel}>
      <Dropdown
        className={st.dropdownComponent}
        aria-label={widgetAriaLabel}
        key={dropdownKey}
        data-hook="currencies-dropdown"
        options={currencyOptions}
        initialSelectedId={selectedCurrency}
        onChange={handleDropdownChange}
      />
    </div>
  );

  const shouldShowPopover = shouldDisplayPopover(
    shouldShowSuggestCurrency,
    isOpen,
    suggestedCurrencyCode,
    selectedCurrency,
    currencies
  );

  useEffect(() => {
    if (shouldShowPopover) {
      onBIEvent(sendBIEventOnPopoverShow({biParams, selectedCurrency, suggestedCurrencyCode}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowPopover]);

  const dropdown = getDropdown();

  return (
    <TPAComponentsProvider value={{mobile: isMobile, rtl: isRtl}}>
      {shouldShowPopover ? (
        <PopoverWrapper
          isMobile={isMobile}
          setIsOpen={setIsOpen}
          onChange={onChange}
          selectedCurrency={selectedCurrency}
          userCountryInfo={userCountryInfo}
          getTranslationByKey={getTranslationByKey}
          sendBIEventOnButtonClick={sendBIEventOnButtonClick}>
          {dropdown}
        </PopoverWrapper>
      ) : (
        dropdown
      )}
    </TPAComponentsProvider>
  );
};
